/**
 * /dashboard/posh/reports — Annual report list (HR-admin generates).
 *
 * Migration note (Phase 3f — 2026-06-01): /dashboard/posh/reports/$id is
 * ported; <a href> swapped for typed Link.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { toApiError } from "@/lib/api";
import { useReports } from "@/hooks/use-posh";
import { fmtDate } from "@/lib/posh-format";
import { reportIsInterim } from "@/lib/posh-report";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { FileBarChart } from "lucide-react";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { ReportGenerateForm } from "@/components/posh/report-generate-form";
import { AwarenessEventsCard } from "@/components/posh/awareness-events-card";
import { usePoshAdmin } from "@/components/posh/use-posh-admin";
import { PoshHrOnlyNotice } from "@/components/posh/posh-hr-only-notice";
import { REPORTS_HR_ONLY_TEXT } from "@/components/posh/posh-policy-errors";
import {
  Card,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/posh/reports/")({
  component: ReportsRoute,
});

function ListSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-1/4" />
            <Skeleton className="mt-2 h-4 w-1/2" />
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

function ReportList() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const isAdmin = usePoshAdmin();
  const q = useReports();
  if (q.isLoading) return <ListSkeleton />;
  if (q.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load reports</AlertTitle>
        <AlertDescription>
          {toApiError(q.error).message}
        </AlertDescription>
      </Alert>
    );
  }
  const reports = q.data?.reports ?? [];
  if (reports.length === 0) {
    return (
      <EmptyState
        icon={FileBarChart}
        title="No Annual Reports Yet"
        description={
          isAdmin
            ? "The POSH Act annual report is a counts-only statutory aggregate, one per calendar year. Use “Generate Report” above to create your first."
            : "The POSH Act annual report is a counts-only statutory aggregate, one per calendar year. Your HR admin generates and files it with the district officer."
        }
      />
    );
  }
  return (
    <div className="space-y-3">
      {[...reports]
        .sort((a, b) => b.year - a.year)
        .map((r) => (
          <Link
            key={r.id}
            to="/app/$tenantSlug/posh/reports/$id"
            params={{tenantSlug, id: String(r.id)}}
            className="group block"
          >
            <Card className="transition-colors group-hover:border-primary/40">
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">
                      Annual Report {r.year}
                    </CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {r.filing_deadline
                        ? `Filing deadline ${fmtDate(r.filing_deadline)}`
                        : "Statutory annual report"}
                      {r.filed_at
                        ? ` · Filed ${fmtDate(r.filed_at)}`
                        : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {reportIsInterim(r) && (
                      <StatusBadge tone="warning" label="Interim" />
                    )}
                    <StatusBadge status={r.status} />
                  </div>
                </div>
              </CardHeader>
            </Card>
          </Link>
        ))}
    </div>
  );
}

function ReportsRoute() {
  const isAdmin = usePoshAdmin();
  // HR-only screen (GET /posh/reports is company_admin / hr_manager) —
  // anyone else gets the pointer, never a raw "Insufficient_role.".
  if (!isAdmin) {
    return (
      <PoshModuleShell title="Annual Reports">
        <PoshFeatureGate>
          <PoshHrOnlyNotice text={REPORTS_HR_ONLY_TEXT} />
        </PoshFeatureGate>
      </PoshModuleShell>
    );
  }
  return (
    <PoshModuleShell
      title="Annual Reports"
      description="Statutory POSH annual reports (counts only — no complainant identities). File with the district officer by 31 January."
      actions={isAdmin ? <ReportGenerateForm /> : undefined}
    >
      <PoshFeatureGate>
        <div className="space-y-5">
          <ReportList />
          {/* CR0.1 — §19(c) workshop register (HR-admin only; the
              backend RBAC-gates the endpoints, so don't fire the query
              for non-admins). Feeds the Rule 14 "workshops conducted"
              count in the annual report + SHe-Box export. */}
          {isAdmin && <AwarenessEventsCard />}
        </div>
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
